/* eslint-disable react/jsx-no-useless-fragment */
import { DSControlledCheckbox } from '@elliemae/ds-controlled-form';
import { FlagSolid, WarningSquare } from '@elliemae/ds-icons';
import { DSPillGroupV2, DSPillV2 } from '@elliemae/ds-pills';
import { DSToolbar } from '@elliemae/ds-toolbar';
import {
  useLoanDataFieldSelectorHook,
  useLookup,
  useLookupOptions,
} from '@frontend/pricing-search';
import { get } from 'lodash';
import { arrayOf, bool, func, shape } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Rights from '../../../../../common/services/Rights';
import {
  EXCEPTION_APPLIED_LABEL,
  hasActiveException,
} from '../../EligibilityException';
import { FilterPills } from '../../FilterPills';
import { GridSettingsMenu } from '../GridSettingsMenu';
import { StyledDSToolbarWrapper } from './styled';

export const EligibleProductsToolbar = ({
  activeFilters,
  onFilterChange,
  refreshPrograms,
  showTarget,
  showFlagged,
  showExceptionsApplied,
  showGridSettings,
  programs,
}) => {
  const requestedTerms = useLoanDataFieldSelectorHook('productType', []) as any;
  const loanTermsLookup = (useLookup('loanTerms') as any[]) || [];
  const [openTermPill, setOpenTermPill] = useState(false);

  const termOptions = useMemo(() => {
    const requested = requestedTerms?.value || [];
    const available = loanTermsLookup.filter((term) =>
      requested.includes(term.value),
    );
    return [{ label: 'All', value: undefined }, ...available].map((term) => ({
      dsId: term.value == null ? 'all' : String(term.value),
      label: term.label,
      // the dropdown marks value as required, so All carries its dsId
      value: term.value ?? 'all',
      type: 'single' as const,
    }));
  }, [loanTermsLookup, requestedTerms?.value]);

  const selectedTerm = activeFilters?.term;
  const selectedTermDsId =
    selectedTerm?.value == null ? 'all' : String(selectedTerm.value);
  const selectedTermLabel =
    termOptions.find((option) => option.dsId === selectedTermDsId)?.label ??
    'All';

  // Fall back to All when the chosen term is no longer among the results.
  useEffect(() => {
    const stillAvailable = termOptions.some(
      (option) => option.dsId === selectedTermDsId,
    );
    if (!stillAvailable) onFilterChange('term', { label: 'All' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termOptions.map((option) => option.dsId).join(','), selectedTermDsId]);

  const investorsOptions = useLookupOptions('investors');
  const standardProducts = useLookupOptions('standardProducts') as any[];
  const requestedProducts = (useLoanDataFieldSelectorHook(
    'standardProducts',
    [],
  ) || []) as any;
  const selectedProducts = standardProducts.filter((obj) =>
    requestedProducts.value.includes(obj.value),
  );
  const specialProducts = useLookupOptions('specialProducts');
  const requestedSpecialProducts = (useLoanDataFieldSelectorHook(
    'specialProducts',
    [],
  ) || []) as any;
  const selectedSpecialProducts = specialProducts.filter((obj) =>
    requestedSpecialProducts.value.includes(obj.value),
  );
  const clientsettings = useLookup('clientSettings', '');
  const flaggedSetting = get(clientsettings, '[0].options.flagged');
  const [checked, setChecked] = useState(
    get(clientsettings, '[0].options.flagged'),
  );
  const initalInvestorSelection = { 0: { id: 0, label: 'All' } };
  const [openInvestorPill, setOpenInvestorPill] = useState(false);
  const [openProductPill, setOpenProductPill] = useState(false);
  const [showProductPill, setShowProductPill] = useState(true);
  const [openSpecialProductPill, setOpenSpecialProductPill] = useState(false);
  const [showSpecialProductPill, setShowSpecialProductPill] = useState(true);
  const [selectedInvestorOptions, setSelectedInvestorOptions] = useState<any>(
    initalInvestorSelection,
  );
  const calcInitial = () => {
    const initialProducts = selectedProducts.reduce((acc, obj) => {
      acc[obj.value] = { id: obj.value, label: obj.label };
      return acc;
    }, {});
    if (Object.keys(initialProducts).length > 0) {
      setShowProductPill(true);
    } else {
      setShowProductPill(false);
    }
    return initialProducts;
  };
  const calcSpecialInitial = () => {
    const initialSpecialProducts = selectedSpecialProducts.reduce(
      (acc, obj) => {
        acc[obj.value] = { id: obj.value, label: obj.label };
        return acc;
      },
      {},
    );
    if (Object.keys(initialSpecialProducts).length > 0) {
      setShowSpecialProductPill(true);
    } else {
      setShowSpecialProductPill(false);
    }
    return initialSpecialProducts;
  };
  const [selectedProductsOptions, setSelectedProductsOptions] =
    useState(calcInitial);
  const [selectedSpecialProductsOptions, setSelectedSpecialProductsOptions] =
    useState(calcSpecialInitial);
  const productOptions = (useLoanDataFieldSelectorHook('productOptions', []) ||
    []) as any[] | any;

  const handleFlaggedChange = (value) => {
    setChecked(value);
    onFilterChange('flagged', value);
  };

  const [checkedExceptions, setCheckedExceptions] = useState(false);
  const handleExceptionsAppliedChange = (value) => {
    setCheckedExceptions(value);
    onFilterChange('exceptionsApplied', value);
  };

  // Clear the "Exception Applied" filter once no exceptions remain.
  useEffect(() => {
    if (
      checkedExceptions &&
      programs?.length > 0 &&
      !hasActiveException(programs)
    ) {
      setCheckedExceptions(false);
      onFilterChange('exceptionsApplied', false);
    }
  }, [checkedExceptions, programs, onFilterChange]);

  const mappedInvestorsOptions = React.useMemo(() => {
    return [
      { dsId: '0', label: 'All', type: 'checkbox' },
      ...(investorsOptions || [])
        .map(({ label, value }) => ({
          dsId: String(value),
          label,
          type: 'checkbox',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [investorsOptions]);

  const mappedProductsOptions = React.useMemo(() => {
    return (selectedProducts || [])
      .map(({ label, value }) => ({
        dsId: String(value),
        label,
        type: 'checkbox',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedProducts]);

  const mappedSpecialProductsOptions = React.useMemo(() => {
    return (selectedSpecialProducts || [])
      .map(({ label, value }) => ({
        dsId: String(value),
        label,
        type: 'checkbox',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedSpecialProducts]);

  const handleInvestorOptionClick = (_, optionObject) => {
    const { dsId, label } = optionObject;
    let optionsObject = { ...selectedInvestorOptions } as any;
    if (dsId === '0' && !optionsObject[dsId]) {
      optionsObject = {};
    }
    if (dsId !== '0' && optionsObject['0']) {
      delete optionsObject['0'];
    }
    if (optionsObject[dsId]) delete optionsObject[dsId];
    else optionsObject[dsId] = { id: Number(dsId), label };
    setSelectedInvestorOptions(optionsObject);
    onFilterChange('investor', Object.values(optionsObject));
  };

  const selectedInvestorOptionsValues = Object.values(
    selectedInvestorOptions,
  ) as any[];

  const handleProductOptionClick = (_, optionObject) => {
    const { dsId, label } = optionObject;
    const optionsObject = { ...selectedProductsOptions };

    if (optionsObject[dsId]) delete optionsObject[dsId];
    else optionsObject[dsId] = { id: Number(dsId), label };
    setSelectedProductsOptions(optionsObject);
    onFilterChange('product', Object.values(optionsObject));
  };

  const selectedProductOptionsValues = Object.values(
    selectedProductsOptions,
  ) as any[];

  const handleSpecialProductOptionClick = (_, optionObject) => {
    const { dsId, label } = optionObject;
    const optionsObject = { ...selectedSpecialProductsOptions };

    if (optionsObject[dsId]) delete optionsObject[dsId];
    else optionsObject[dsId] = { id: Number(dsId), label };
    setSelectedSpecialProductsOptions(optionsObject);
    onFilterChange('special', Object.values(optionsObject));
  };

  const selectedSpecialProductOptionsValues = Object.values(
    selectedSpecialProductsOptions,
  ) as any[];

  const getTooltipValue = (arr) => {
    return arr
      .slice(3)
      .map((val) => val.label)
      .join(', ');
  };

  const handleInvestorPillRemove = (id) => {
    const optionsObject = { ...selectedInvestorOptions };
    delete optionsObject[id];

    setSelectedInvestorOptions(optionsObject);
    onFilterChange('investor', Object.values(optionsObject));
  };

  const handleProductPillRemove = (id) => {
    const optionsObject = { ...selectedProductsOptions };
    delete optionsObject[id];

    setSelectedProductsOptions(optionsObject);
    onFilterChange('product', Object.values(optionsObject));
  };

  const handleSpecialProductPillRemove = (id) => {
    const optionsObject = { ...selectedSpecialProductsOptions };
    delete optionsObject[id];

    setSelectedSpecialProductsOptions(optionsObject);
    onFilterChange('special', Object.values(optionsObject));
  };

  useEffect(() => {
    const initialProducts = selectedProducts.reduce((acc, obj) => {
      acc[obj.value] = { id: obj.value, label: obj.label };
      return acc;
    }, {});
    setShowProductPill(Object.keys(initialProducts).length > 0);
    setSelectedProductsOptions(initialProducts);

    const initialSpecialProducts = selectedSpecialProducts.reduce(
      (acc, obj) => {
        acc[obj.value] = { id: obj.value, label: obj.label };
        return acc;
      },
      {},
    );
    setShowSpecialProductPill(Object.keys(initialSpecialProducts).length > 0);
    setSelectedSpecialProductsOptions(initialSpecialProducts);

    setSelectedInvestorOptions(initalInvestorSelection);
    onFilterChange('product', Object.values(initialProducts));
    onFilterChange('special', Object.values(initialSpecialProducts));
    onFilterChange('investor', Object.values(initalInvestorSelection));

    setChecked(flaggedSetting);
    handleFlaggedChange(flaggedSetting);
  }, [refreshPrograms]);

  return (
    <StyledDSToolbarWrapper>
      <DSToolbar withDepth={false}>
        <div
          style={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            style={{
              alignItems: 'flex-start',
              display: 'flex',
            }}
          >
            <div style={{ marginRight: '2px' }}>
              <DSPillGroupV2>
                <DSPillV2
                  label="Term Types"
                  type="dropdown"
                  onDropdownClick={() => setOpenTermPill(true)}
                  dropdownProps={{
                    options: termOptions,
                    selectedOptions: { [selectedTermDsId]: true },
                    isOpened: openTermPill,
                    onClickOutside: () => setOpenTermPill(false),
                    onOptionClick: (_next, option) => {
                      onFilterChange(
                        'term',
                        option.dsId === 'all'
                          ? { label: 'All' }
                          : { label: option.label, value: option.value },
                      );
                      setOpenTermPill(false);
                    },
                  }}
                />
                <DSPillV2 label={selectedTermLabel} type="value" />
              </DSPillGroupV2>
            </div>
            {showTarget && <FilterPills />}
            {Rights.viewTrueInvestorName && (
              <div style={{ marginRight: '2px' }}>
                <DSPillGroupV2>
                  <DSPillV2
                    label="Investor"
                    type="dropdown"
                    onDropdownClick={() => setOpenInvestorPill(true)}
                    dropdownProps={{
                      options: Object.values(mappedInvestorsOptions),
                      selectedOptions: selectedInvestorOptions,
                      isOpened: openInvestorPill,
                      onClickOutside: () => setOpenInvestorPill(false),
                      onOptionClick: handleInvestorOptionClick,
                    }}
                  />
                  {selectedInvestorOptionsValues.map(({ id, label }, index) => {
                    if (index < 3)
                      return (
                        <DSPillV2
                          key={id}
                          label={label}
                          type="removable"
                          onRemove={() => handleInvestorPillRemove(id)}
                        />
                      );
                    if (index === 3)
                      return (
                        <DSPillV2
                          tooltipValue={getTooltipValue(
                            selectedInvestorOptionsValues,
                          )}
                          label={`+ ${
                            selectedInvestorOptionsValues.length - index
                          }`}
                          type="value"
                        />
                      );

                    return null;
                  })}
                </DSPillGroupV2>
              </div>
            )}
            {showProductPill && (
              <div style={{ marginRight: '2px' }}>
                <DSPillGroupV2>
                  <DSPillV2
                    label="Standard Products"
                    type="dropdown"
                    onDropdownClick={() => setOpenProductPill(true)}
                    dropdownProps={{
                      options: Object.values(mappedProductsOptions) as any[],
                      selectedOptions: selectedProductsOptions,
                      isOpened: openProductPill,
                      onClickOutside: () => setOpenProductPill(false),
                      onOptionClick: handleProductOptionClick,
                    }}
                  />
                  {selectedProductOptionsValues.map(({ id, label }, index) => {
                    if (index < 3)
                      return (
                        <DSPillV2
                          key={id}
                          label={label}
                          type="removable"
                          onRemove={() => handleProductPillRemove(id)}
                        />
                      );
                    if (index === 3)
                      return (
                        <DSPillV2
                          tooltipValue={getTooltipValue(
                            selectedProductOptionsValues,
                          )}
                          label={`+ ${
                            selectedProductOptionsValues.length - index
                          }`}
                          type="value"
                        />
                      );
                    return null;
                  })}
                </DSPillGroupV2>
              </div>
            )}
            {showSpecialProductPill && (
              <div style={{ marginRight: '2px' }}>
                <DSPillGroupV2>
                  <DSPillV2
                    label="Special Products"
                    type="dropdown"
                    onDropdownClick={() => setOpenSpecialProductPill(true)}
                    dropdownProps={{
                      options: Object.values(mappedSpecialProductsOptions),
                      selectedOptions: selectedSpecialProductsOptions,
                      isOpened: openSpecialProductPill,
                      onClickOutside: () => setOpenSpecialProductPill(false),
                      onOptionClick: handleSpecialProductOptionClick,
                    }}
                  />
                  {selectedSpecialProductOptionsValues.map(
                    ({ id, label }, index) => {
                      if (index < 3)
                        return (
                          <DSPillV2
                            key={id}
                            label={label}
                            type="removable"
                            onRemove={() => handleSpecialProductPillRemove(id)}
                          />
                        );
                      if (index === 3)
                        return (
                          <DSPillV2
                            tooltipValue={getTooltipValue(
                              selectedSpecialProductOptionsValues,
                            )}
                            label={`+ ${
                              selectedSpecialProductOptionsValues.length - index
                            }`}
                            type="value"
                          />
                        );
                      return null;
                    },
                  )}
                </DSPillGroupV2>
              </div>
            )}
            {productOptions.value.length > 0 && showFlagged && (
              <>
                <div
                  style={{
                    display: 'inline-flex',
                    marginTop: '4px',
                    marginLeft: '3px',
                  }}
                >
                  <DSControlledCheckbox
                    data-testid="custom-data-testid"
                    checked={checked}
                    name="flagged"
                    label=""
                    id="flaggedCheckbox"
                    onChange={(value) => handleFlaggedChange(value)}
                  />

                  <FlagSolid
                    size="s"
                    color={['danger', '900']}
                    style={{ marginLeft: '3px' }}
                  />
                  <p style={{ marginTop: '1px', marginLeft: '3px' }}>Flagged</p>
                </div>
              </>
            )}
            {showExceptionsApplied && hasActiveException(programs) && (
              <div
                style={{
                  display: 'inline-flex',
                  marginTop: '4px',
                  marginLeft: '12px',
                }}
              >
                <DSControlledCheckbox
                  checked={checkedExceptions}
                  name="exceptionsApplied"
                  label=""
                  id="exceptionsAppliedCheckbox"
                  onChange={(value) => handleExceptionsAppliedChange(value)}
                />
                <WarningSquare
                  size="s"
                  color={['warning', '500']}
                  style={{ marginLeft: '3px' }}
                />
                <p style={{ marginTop: '1px', marginLeft: '3px' }}>
                  {EXCEPTION_APPLIED_LABEL}
                </p>
              </div>
            )}
          </div>
          {showGridSettings && (
            <div style={{ marginLeft: 'auto', display: 'inline-flex' }}>
              <GridSettingsMenu />
            </div>
          )}
        </div>
      </DSToolbar>
    </StyledDSToolbarWrapper>
  );
};

EligibleProductsToolbar.defaultProps = {
  activeFilters: {},
  refreshPrograms: false,
  showTarget: false,
  showFlagged: false,
  showExceptionsApplied: false,
  showGridSettings: false,
  onFilterChange: () => null,
  programs: [],
};

EligibleProductsToolbar.propTypes = {
  activeFilters: shape({}),
  refreshPrograms: bool,
  showTarget: bool,
  showFlagged: bool,
  showExceptionsApplied: bool,
  showGridSettings: bool,
  onFilterChange: func,
  programs: arrayOf(shape({})),
};
